import { useMemo } from "react";

import type { User } from "@sentry/react";

import { defaultPrivateSpaceName, defaultTeamSpaceName } from "../../config";
import useAccount from "../../hooks/useAccount";
import type { ProjectViewLink, Space } from "../../pages/ViewLinkListPage/ViewLinkListPage";
import { CollaborationPromo } from "../CollaborationPromo/CollaborationPromo";
import { type Link, LinkList, type LinkListTelemetryLocation } from "../LinkList/LinkList";
import { UnpublishConfirmationModal } from "../UnpublishConfirmationModal/UnpublishConfirmationModal";

const mapSpaceName = (space?: Space) => {
  if (!space) return "-";
  if (space.spaceName) return space.spaceName;
  if (space.spaceType === "private") return defaultPrivateSpaceName;
  return defaultTeamSpaceName;
};

interface ViewLinkListProps {
  viewLinks: ProjectViewLink[];
  users: User[];
  isShortVersion?: boolean;
  isUnpublishHidden?: boolean;
  telemetryLocation: LinkListTelemetryLocation;
}

export const ViewLinkList: React.FC<ViewLinkListProps> = ({
  viewLinks,
  users,
  isShortVersion,
  telemetryLocation,
  isUnpublishHidden,
}) => {
  const { data: accountData } = useAccount();

  const mappedViewLinks = useMemo(() => {
    return viewLinks.map((viewLink) => {
      return {
        ...viewLink,
        location: mapSpaceName(viewLink.space),
        id: viewLink.projectViewLinkId,
        relativeURL: `/v/${viewLink.projectViewLinkId}`,
        type: "view_link",
      } satisfies Link;
    });
  }, [viewLinks]);

  const showPromo = Boolean(
    mappedViewLinks.length === 0 ||
      (!isShortVersion &&
        mappedViewLinks.length === 1 &&
        accountData &&
        mappedViewLinks?.[0]?.uploadedByUserId !== accountData.id)
  );

  return (
    <LinkList
      links={mappedViewLinks}
      users={users}
      telemetryLocation={telemetryLocation}
      showPromo={showPromo}
      promoComponent={<CollaborationPromo />}
      {...(!isUnpublishHidden && { UnpublishModalComponent: UnpublishConfirmationModal })}
      isShortVersion={isShortVersion}
    />
  );
};
